import each from 'lodash/each';
import { capitalizeFirstLetter } from './util';
import { getAngularModuleDependencies } from './component-helper';
import getTokenNameByUrl from './api-token-helper';
import log from '../../loader-base/src/helpers/log-helper';

const morningstarNgApp = 'MorningstarApp';

/*
* Set API tokens in asterix context object.
* @param {object} initialisation object with apiTokens property
*/
function setAPITokens(loaderConfig) {
    if (morningstar.asterix && morningstar.asterix.context && typeof morningstar.asterix.context.setToken === 'function') {
        const tokens = loaderConfig.apiTokens || {};

        Object.keys(tokens).forEach((tokenKey) => {
            morningstar.asterix.context.setToken(tokenKey, tokens[tokenKey]);
        });
    }
}

/**
 * Extract log level and log type value from a pipe separated string
 * @param {*} logLevel
 */
function extractLogLevelFromString(logLevel) {
    const logLevels = logLevel.split('|');
    let value = '';
    let result = 0;
    if (logLevels.length === 1 && morningstar.asterix.instrumentation.LOG_LEVEL[logLevels[0].toUpperCase()]) {
        return morningstar.asterix.instrumentation.LOG_LEVEL[logLevels[0].toUpperCase()];
    }

    Object.keys(logLevels).forEach((key) => {
        value = logLevels[key].trim();
        result |= morningstar.asterix.instrumentation.LOG_TYPE[value.toUpperCase()];
    });

    return result;
}

/**
 * Register log level provider using a friendly version of the provider name. It appends the LogProvider suffix to tne name and capitalize the firts char.
 * @param {*} providerName
 * @param {*} logLevel
 */
function registerLogProvider(providerName, logLevel) {
    morningstar.asterix.instrumentation.registerLogProvider(providerName, new morningstar.asterix.providers[capitalizeFirstLetter(`${providerName}LogProvider`)](), {
        logLevel,
    });
}

function trackComponentMetadata(manifest) {
    if (manifest.config && manifest.config.components) {
        Object.keys(manifest.config.components).forEach((modulePath) => {
            const componentConfig = manifest.config.components[modulePath];
            if (componentConfig.type) {
                let resolvedVersion = '';
                let resolvedInstId = '';
                const componentTypeDashCase = morningstar.asterix.util.string.dashCase(componentConfig.type);
                if (manifest.loaderConfig.resolvedDependencies && manifest.loaderConfig.resolvedDependencies[componentTypeDashCase]) {
                    resolvedVersion = manifest.loaderConfig.resolvedDependencies[componentTypeDashCase].version;
                    resolvedInstId = manifest.loaderConfig.resolvedDependencies[componentTypeDashCase].instid;
                }
                morningstar.asterix.usageTracker.trackHit({
                    type: 'event',
                    category: componentConfig.type,
                    action: 'componentDownloaded',
                    label: 'Component downloaded',
                    value: `${resolvedInstId}:${resolvedVersion}:${componentConfig.blueprint || ''}`,
                    componentModulePath: modulePath,
                });
            }
        });
    }
}

/**
* Set the instrumentation options after checking if the object is correctly set.
* @param {*} instrumentation
*/
function setInstrumentationOptions(instrumentation) {
    let logLevel;
    if (window.morningstar && window.morningstar.asterix) {
        logLevel = instrumentation ? instrumentation.level : undefined;
        /* extend the LOG_LEVEL and LOG_TYPE to morningstar.asterix.instrumentation.LOG_* */
        if (logLevel) {
            logLevel = extractLogLevelFromString(logLevel);
            // log.info("ec-loader set logLevel value:", logLevel, " for ", instrumentation.provider, " provider.");
        } else {
            logLevel = morningstar.asterix.instrumentation.LOG_LEVEL.DEFAULT;
            // log.info("ec-loader set logLevel:", logLevel, " for ", instrumentation.provider, " provider.");
        }
        /* register the console provider with default log level */
        registerLogProvider(instrumentation.provider, logLevel);
        return true;
    }
    log.warn("instrumentation can't be initialized because asterix is not yet defined at this stage");
    return false;
}

/**
 * Get asterix config object from manifest
 * @param {*} manifest
 */
function getAsterixConfigOptions(manifest) {
    const { loaderConfig } = manifest;

    const configOption = {};

    configOption.env = loaderConfig.runtime.environment === 'prod' ? 'production' : loaderConfig.runtime.environment;
    configOption.environment = configOption.env;
    configOption.appConfig = manifest.config;
    configOption.instid = loaderConfig.componentSource && loaderConfig.componentSource.instid ? loaderConfig.componentSource.instid : null;
    configOption.instrumentation = loaderConfig.instrumentation.providers;
    configOption.userIdentity = loaderConfig.userIdentity;
    configOption.apiTokens = loaderConfig.apiTokens;
    each(loaderConfig.additionalAsterixConfigOptions, (value, key) => {
        configOption[key] = value;
    });

    return configOption;
}

/**
 * Create component tag
 * @param {*} config - application config
 * @param {*} morningstarComponentId - morningstar component id
 * @param {*} container - the element where we will place the new component tag
 */
function createComponentTag(config, morningstarComponentId, container) {
    if (config.components[morningstarComponentId] && morningstar.asterix) {
        const instanceConfig = morningstar.asterix.config.getInstanceConfig(morningstarComponentId);
        if (instanceConfig) {
            const componentType = morningstar.asterix.util.string.dashCase(instanceConfig.type);
            if (/^ec-/.test(componentType)) { // mean it is a EC component will build the tag
                const componentNode = document.createElement(componentType);
                componentNode.setAttribute('mstar-component-id', morningstarComponentId);
                if (morningstar.asterix.util.isDomElement(container)) {
                    container.appendChild(componentNode);
                } else {
                    // log.debug('params.container is not a object.');
                }
                return morningstar.asterix.instanceRegistry.get(morningstarComponentId);
            }
        } else {
            return null;
        }
    }
    return null;
}

/**
 * Set the locationProvider option to enable html5 mode
 * @param {*} mode
 */
function setHtml5ModeOptions(locationProviderOptions) {
    const enabled = locationProviderOptions.html5ModeEnabled === true;
    const requireBase = locationProviderOptions.requireBase === true;
    const rewriteLinks = locationProviderOptions.rewriteLinks === true;
    const { hashPrefix } = locationProviderOptions;

    angular.module('mstarCore').config(($locationProvider) => {
        // log.info('update locationProvider html5Mode settings to:', 'enabled:', enabled, 'requireBase:', requireBase, 'rewriteLinks:', rewriteLinks, "with prefix:", hashPrefix);
        const html5ModeSettings = {
            enabled,
            requireBase,
            rewriteLinks,
        };
        if (hashPrefix) {
            $locationProvider.html5Mode(html5ModeSettings).hashPrefix('!');
        } else {
            $locationProvider.html5Mode(html5ModeSettings);
        }
    });
}

/**
 * Instantiates Asterix with all configuration options and also instantiates all other EC-components.
 * @param  {*} manifest - Contains the component dependencies for the top level components,
 * the app configuration and resolutions together with the loader configuration.
 * @returns {Promise} - Resolves with the manifest
 */
const ecReady = function ecReady(manifest) {
    return new Promise((resolve) => {
        const { loaderConfig } = manifest;

        const bootstrap = loaderConfig.angular.bootstrap !== false;
        const { angular } = window;

        if (angular && !bootstrap) {
            const ngModules = getAngularModuleDependencies(manifest.resolutions);
            if (ngModules.length > 0) {
                angular.module(morningstarNgApp, ngModules);
            }
        }

        setAPITokens(loaderConfig);

        each(loaderConfig.instrumentation.providers, (instrumentationObject) => {
            try {
                setInstrumentationOptions(instrumentationObject);
            } catch (e) {
                // log.error(e); TODO
                console.log('setInstrumentationOptions error: ');
                console.log(e);
            }
        });

        if (morningstar &&
            morningstar.asterix &&
            morningstar.asterix.instrumentation &&
            loaderConfig.userIdentity) {
            const identityObject = {};
            if (loaderConfig.configuration) {
                identityObject.namespace = loaderConfig.configuration.namespace || '';
                identityObject.environment = loaderConfig.configuration.environment || '';
                identityObject.configVersion = loaderConfig.configuration.source && loaderConfig.configuration.source.version;
            }
            if (loaderConfig.userIdentity) {
                identityObject.userId = loaderConfig.userIdentity.userId || '';
                identityObject.sessionId = loaderConfig.userIdentity.sessionId || '';
            }
            if (window.navigator) {
                identityObject.userAgent = window.navigator.userAgent || '';
                identityObject.appName = window.navigator.appName || '';
                identityObject.appVersion = window.navigator.appVersion || '';
                identityObject.platform = window.navigator.platform || '';
                identityObject.language = window.navigator.language || '';
                identityObject.languages = window.navigator.languages || '';
                identityObject.cookieEnabled = window.navigator.cookieEnabled || '';
                identityObject.connection = window.navigator.connection || '';
                identityObject.plugins = window.navigator.plugins || '';
            }
            if (window.location) {
                identityObject.href = window.location.toString() || '';
            }
            morningstar.asterix.instrumentation.updateIdentity(identityObject);
        }

        if (loaderConfig.morningstarUsageTracking && loaderConfig.morningstarUsageTracking.enabled && loaderConfig.morningstarUsageTracking.applicationId) {
            try {
                loaderConfig.usageTracking = loaderConfig.usageTracking || [];
                loaderConfig.usageTracking.push({ provider: 'newRelic' });
                const appContext = {};
                if (loaderConfig.configuration) {
                    appContext.namespace = loaderConfig.configuration.namespace || '';
                    appContext.environment = loaderConfig.configuration.environment || '';
                    appContext.configVersion = loaderConfig.configuration.source && loaderConfig.configuration.source.version;
                }
                if (loaderConfig.userIdentity) {
                    appContext.userIdentity = {
                        userId: loaderConfig.userIdentity.userId || '',
                        sessionId: loaderConfig.userIdentity.sessionId || '',
                    };
                }
                if (morningstar.asterix.usageTracker.setAppContext) {
                    morningstar.asterix.usageTracker.setAppContext(appContext);
                } else {
                    console.warn('appContext is not supported');
                }
            } catch (e) {
                console.error(e);
            }
        }

        let apiTokenExpiredCallback;
        if (manifest.loaderConfig && manifest.loaderConfig.api && manifest.loaderConfig.api.apiTokenExpiredCallback && typeof manifest.loaderConfig.api.apiTokenExpiredCallback === 'function') {
            apiTokenExpiredCallback = function apiTokenExpiredCallbackFunction(options, cb) {
                options.token = getTokenNameByUrl(options.url);
                manifest.loaderConfig.api.apiTokenExpiredCallback(options, cb);
            };
        }

        if (loaderConfig.usageTracking) {
            if (window.morningstar && window.morningstar.asterix) {
                Object.keys(loaderConfig.usageTracking).forEach((providerKey) => {
                    try {
                        let { provider } = loaderConfig.usageTracking[providerKey];
                        let providerName;
                        // so provider can be an object or function thereby meaning its not a built-in provider
                        if (typeof provider === 'string') {
                            providerName = provider + providerKey.toString();
                            provider = new morningstar.asterix.providers[capitalizeFirstLetter(`${provider}UsageTrackingProvider`)]();
                        } else {
                            providerName = `custom${providerKey.toString()}`;
                        }
                        delete loaderConfig.usageTracking[providerKey].provider;
                        const options = loaderConfig.usageTracking[providerKey];
                        morningstar.asterix.usageTracker.registerUsageTrackingProvider(providerName, provider, options);
                    } catch (e) {
                        console.error('Error registering usage-tracking provider', e, loaderConfig.usageTracking[providerKey]);
                    }
                });
            } else if (loaderConfig.usageTracking.length) {
                console.warn('Unable to register usage-tracking providers since "window.morningstar.asterix" is not defined');
            }
        }

        morningstar.asterix.bootstrap.init({
            appConfig: manifest.config,
            angularOptions: {
                bootstrap: false,
            },
            configOptions: getAsterixConfigOptions(manifest),
            apiTokenExpiredCallback,
        });

        each(loaderConfig.angular.elementBindings, (elementBinding) => {
            createComponentTag(manifest.config, elementBinding.morningstarComponentId, elementBinding.container);
        });

        if (loaderConfig.angular && loaderConfig.angular.locationProviderOptions && angular) {
            setHtml5ModeOptions(loaderConfig.angular.locationProviderOptions);
        }

        if (angular && bootstrap && morningstar.asterix.ng) {
            angular.element(document).ready(() => {
                const bootstrapElement = Object.prototype.hasOwnProperty.call(loaderConfig.angular, 'bootstrapElement') && loaderConfig.angular.bootstrapElement
                    ? loaderConfig.angular.bootstrapElement : document;
                angular.bootstrap(bootstrapElement, morningstar.asterix.ng.getAngularModuleDependencies());
            });
        }

        trackComponentMetadata(manifest);

        resolve(manifest);
    });
};

export { ecReady as default };



// WEBPACK FOOTER //
// ./src/initialize-ec.js